import {Injectable, OnDestroy} from '@angular/core';
import {concatMap, defer, EMPTY, Observable, Subject} from 'rxjs';
import {catchError, takeUntil, tap} from 'rxjs/operators';
import {hook} from '../../../common/rxjs/hook';
import {destroy$} from '../../destroyable';
import {DynamicZyxDaemonService} from '../../dynamic-xyz/dynamic-zyx-daemon.service';
import {DeliverableId} from '../dex-streaming/types/deliverable';
import {BalanceFetchingTrigger, BalanceFetchingTriggerEvent} from './balance-fetching-trigger';
import {DeliverableBalance} from './deliverable-balance';
import {Erc20BalancesService} from './erc20-balances.service';

interface UpdatesSubsRec {
    wAddresses: string[] | undefined; // Wallet addresses, undefined means 'any'
    dIds: DeliverableId[] | undefined; // undefined means 'any'
    stream$: Subject<PartialBalanceUpdate>;
}

export interface PartialBalanceUpdate {
    wAddress: string;
    tokenBalances: DeliverableBalance[];
}

@Injectable()
export class Erc20BalanceUpdatesService implements OnDestroy, BalanceFetchingTrigger {

    private readonly destroy$ = destroy$();

    private readonly updatesSubsRecs = new Set<UpdatesSubsRec>();

    private readonly triggerFetching$ = new Subject<BalanceFetchingTriggerEvent>();

    constructor(
        private readonly erc20BalancesService: Erc20BalancesService,
        private readonly dynamicZyxDaemonService: DynamicZyxDaemonService
    ) {
        this.initFetching();
    }

    public ngOnDestroy(): void {
        this.updatesSubsRecs.clear();
    }

    public getUpdatesStream(params: {
        wAddresses?: string[];
        dIds?: DeliverableId[];
    }): Observable<PartialBalanceUpdate> {
        const rec: UpdatesSubsRec = {
            wAddresses: params.wAddresses || undefined,
            dIds:     params.dIds ? [...params.dIds] : undefined,
            stream$:  new Subject()
        };

        return rec.stream$.pipe(
            hook({
                subscribe:   () => this.updatesSubsRecs.add(rec),
                unsubscribe: () => this.updatesSubsRecs.delete(rec)
            })
        );
    }

    /**
    * It's called by some trigger, like deposit/withdraw.
    * It should check if there are subscribers matching the passed params.
    * If there is at least one such subscriber, then collect the intersection of
    * subscribed params and the passed params.
    * Fetches balances for the resulting params (use Erc20BalancesService).
    * Then notify subscribers with the results that they subscribed to.
    */
    public notifyUpdates(params: BalanceFetchingTriggerEvent): void {
        if (!this.updatesSubsRecs.size) {
            return;
        }

        const matchingSubscribers: UpdatesSubsRec[] = [];

        // Find all subscribers that match the notification params
        for (const subsRec of this.updatesSubsRecs) {
            // For each wallet address in notification params, check if it matches this subscriber
            const wAddressesToCheck = params.wAddresses || [undefined];

            for (const wAddress of wAddressesToCheck) {
                if (this.isSubsRecMatch({
                    subsRec,
                    wAddress,
                    dIds: params.dIds
                })) {
                    matchingSubscribers.push(subsRec);
                    break; // This subscriber matches, no need to check other wallet addresses
                }
            }
        }

        // If no matching subscribers, exit early
        if (matchingSubscribers.length === 0) {
            return;
        }


        let wAddressesSet: Set<string> | undefined = new Set<string>();
        for (const sub of matchingSubscribers) {
            if (!sub.wAddresses) {
                if (params.wAddresses) {
                    wAddressesSet.clear();
                    params.wAddresses.forEach(addr => wAddressesSet.add(addr));
                } else {
                    wAddressesSet = undefined;
                }

                break;
            }

            sub.wAddresses.forEach(addr => wAddressesSet.add(addr));
        }

        let dIdsSet: Set<DeliverableId> | undefined = new Set<DeliverableId>();
        for (const sub of matchingSubscribers) {
            if (!sub.dIds) {
                if (params.dIds) {
                    dIdsSet.clear();
                    params.dIds.forEach(addr => dIdsSet.add(addr));
                } else {
                    dIdsSet = undefined;
                }

                break;
            }

            sub.dIds.forEach(dId => dIdsSet.add(dId));
        }

        const wAddresses = wAddressesSet ? Array.from(wAddressesSet) : this.getAllWalletAddresses();
        const dIds: DeliverableId[] | undefined = dIdsSet && Array.from(dIdsSet);

        this.triggerFetching$.next({wAddresses, dIds});
    }

    private isSubsRecMatch(params: {
        subsRec: UpdatesSubsRec;
        wAddress?: string;
        dIds?: DeliverableId[];
    }): boolean {
        const { subsRec, wAddress, dIds } = params;

        // If the subscriber or notification has no specific criteria, they match
        if (!wAddress && !dIds || !subsRec.wAddresses && !subsRec.dIds) {
            return true;
        }

        // Check wallet address match
        if (wAddress && subsRec.wAddresses && !subsRec.wAddresses.includes(wAddress)) {
            return false;
        }

        // Check deliverable IDs match
        if (dIds && subsRec.dIds) {
            // Check if there's any intersection between the two arrays
            const hasIntersection = dIds.some(dId => subsRec.dIds!.includes(dId));
            if (!hasIntersection) {
                return false;
            }
        }

        // If we reach here, the subscriber matches the notification criteria
        return true;
    }

    private getAllWalletAddresses(): string[] {
        return this.dynamicZyxDaemonService.dynRemote.userWallets$.value?.map(w => w.address);
    }

    private initFetching(): void {
        this.triggerFetching$.pipe(
            concatMap(event => defer(() => this.fetchAndNotify(event.wAddresses, event.dIds))),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private fetchAndNotify(wAddresses?: string[], dIds?: DeliverableId[]): Observable<any> {
        return this.erc20BalancesService.fetchWalletsTokenBalances(wAddresses, dIds).pipe(
            tap(balancesByWallet => {
                this.updatesSubsRecs.forEach(subsRec => {
                    const wAddressesToNotify = subsRec.wAddresses || wAddresses;
                    const dIdsToNotifySet = new Set<DeliverableId>(subsRec.dIds || dIds);

                    wAddressesToNotify.forEach(wAddress => {
                        const balances = balancesByWallet[wAddress];
                        if (!balances) return;

                        const filteredBalances = balances.filter(balance =>
                            dIdsToNotifySet.has(balance.deliverable.deliverableId)
                        );

                        if (filteredBalances.length > 0) {
                            subsRec.stream$.next({
                                wAddress,
                                tokenBalances: filteredBalances
                            });
                        }
                    });
                });
            }),
            catchError(error => {
                console.error('Error fetching balances for updates:', error);

                return EMPTY;
            })
        );
    }

}
